import React from 'react';
import { Grid, Typography, FormControl, Select, MenuItem, Button, makeStyles } from '@material-ui/core';

const useStyles = makeStyles((theme) => ({
  selectRoot: {
    '&:focus': {
      backgroundColor: 'transparent !important',
    },
    '&.Mui-focused': {
      backgroundColor: 'transparent !important',
    },
    '& .MuiSelect-select:focus': {
      backgroundColor: 'transparent !important',
    },
  },
  formControl: {
    '& .MuiOutlinedInput-root': {
      '&:hover fieldset': {
        borderColor: 'rgba(0, 0, 0, 0.23)',
      },
      '&.Mui-focused fieldset': {
        borderColor: 'rgba(0, 0, 0, 0.23)',
      },
      '&.Mui-focused': {
        backgroundColor: 'transparent !important',
      },
    },
  },
}));

const FilterSection = ({
  classes,
  cmoSite,
  setCmoSite,
  cmo,
  setCmo,
  site,
  setSite,
  cmoOrgId,
  setCmoOrgId,
  deliverableType,
  setDeliverableType,
  initialCmoSite,
  initialCmoOrgId,
  initialDeliverableType,
  cmoMembersList,
  mfgPlantsList,
  deliverableTypesList,
  filtersLoading,
  handleSaveFilters,
  handleClearFilters,
}) => {
  const localClasses = useStyles();

  // CMO options come from fetchCMOMembers (same source as the Purchase Order
  // Dashboard): each member is { organization_id, organization_name }. The
  // dropdown value is organization_id (the CMO id, also sent as cdmoOrgID and
  // used to narrow the Site list); the label is organization_name.
  const cmoOptions = React.useMemo(() => {
    const seen = new Set();
    const list = [];
    (cmoMembersList || []).forEach((m) => {
      if (!m || m.organization_id == null) return;
      const id = String(m.organization_id);
      if (seen.has(id)) return;
      seen.add(id);
      list.push({ id, name: m.organization_name || id });
    });
    return list;
  }, [cmoMembersList]);

  // Site options come from fetchMfgPlants. The Site name is the parent CMO
  // site's name — SiteName (cmo_sites.siteShortName) — with mfgPlantShortName
  // kept as a fallback for plant rows not yet linked to a site. CMO = "All" →
  // every site; a specific CMO → only sites whose vendorMosymphonyID matches
  // that CMO's organization_id. Deduped by name for display.
  const getSiteName = (p) =>
    (p && p.SiteName != null && String(p.SiteName).trim() !== ''
      ? String(p.SiteName)
      : p && p.mfgPlantShortName != null
        ? String(p.mfgPlantShortName)
        : ''
    ).trim();

  // Plant short name shown alongside the site (e.g. "HYD / Unit II") — display
  // only. The filter's underlying value stays the site name, matching what
  // handleSiteChange and the dashboard payload already expect; a site with more
  // than one plant just shows the first plant found under it.
  const getPlantShortName = (p) =>
    (p && p.mfgPlantShortName != null ? String(p.mfgPlantShortName) : '').trim();

  const siteOptions = React.useMemo(() => {
    const selectedOrgId = cmo && cmo !== 'all' ? String(cmo) : null;
    const seen = new Set();
    const list = [];
    (mfgPlantsList || []).forEach((p) => {
      const name = getSiteName(p);
      if (!name) return;
      if (selectedOrgId != null && String(p.vendorMosymphonyID) !== selectedOrgId) return;
      if (seen.has(name)) return;
      seen.add(name);
      const plant = getPlantShortName(p);
      list.push({ value: name, label: plant ? `${name} / ${plant}` : name });
    });
    return list;
  }, [mfgPlantsList, cmo]);

  const handleCmoChange = (newCmo) => {
    setCmo(newCmo);
    // Site options depend on the CMO, so reset the Site whenever the CMO changes.
    setSite('all');
    setCmoSite('all');
    // The CMO's organization_id is the cdmoOrgID; keeps the filter applied even
    // while Site stays "All". (`cmo` already holds that id.)
    setCmoOrgId(!newCmo || newCmo === 'all' ? 'all' : String(newCmo));
  };

  const handleSiteChange = (newSite) => {
    setSite(newSite);
    if (!newSite || newSite === 'all') {
      // Site → "All": keep any CMO already chosen, just clear the site name.
      setCmoSite('all');
      return;
    }
    // Canonical cmoSite value is the SiteName (sent as `cmoSite`).
    setCmoSite(newSite);
    // Selecting a Site also selects its CMO: resolve the plant's
    // vendorMosymphonyID (= the CMO's organization_id) and apply it so
    // cdmoOrgID is sent to the payload and the CMO dropdown reflects it. With a
    // CMO already chosen, match the plant within that CMO's org.
    const selectedOrgId = cmo && cmo !== 'all' ? String(cmo) : null;
    const plant = (mfgPlantsList || []).find((p) => {
      const name = getSiteName(p);
      if (name !== newSite) return false;
      if (selectedOrgId != null) return String(p.vendorMosymphonyID) === selectedOrgId;
      return true;
    });
    if (plant && plant.vendorMosymphonyID != null) {
      const orgId = String(plant.vendorMosymphonyID);
      setCmo(orgId);
      setCmoOrgId(orgId);
    }
  };

  const filtersDisabled = (cmoMembersList || []).length === 0 || filtersLoading;
  const siteDisabled = (mfgPlantsList || []).length === 0 || filtersLoading;

  return (
    <Grid
      item
      spacing={1}
      style={{
        display: 'flex',
        justifyContent: 'flex-start',
        flexDirection: 'row',
        gap: '0.5rem',
        alignItems: 'flex-end',
      }}
    >
      {/* CMO filter */}
      <Grid
        style={{
          display: 'flex',
          alignItems: 'flex-start',
          gap: '10px',
          flexDirection: 'column',
          marginTop: '10px',
        }}
      >
        <Grid>
          <Typography style={{ fontWeight: 'bold', fontSize: '0.875rem' }} className="dashboard-filter-labels">
            CMO:
          </Typography>
        </Grid>
        <Grid>
          <FormControl
            variant="outlined"
            size="small"
            style={{ width: '14rem', marginRight: '0.5rem' }}
            className={`dashboard-filter-dropdown ${localClasses.formControl}`}
          >
            <Select
              value={cmo || 'all'}
              onChange={(e) => handleCmoChange(e.target.value)}
              style={{ height: '32px', backgroundColor: 'transparent', fontSize: '0.875rem' }}
              disabled={filtersDisabled}
              displayEmpty
              disableRipple
              disableFocusRipple
              className={localClasses.selectRoot}
              MenuProps={{ PaperProps: { style: { maxHeight: 300 } } }}
            >
              <MenuItem value="all">All</MenuItem>
              {cmoOptions.length === 0 ? (
                <MenuItem value="" disabled>No data available</MenuItem>
              ) : (
                cmoOptions.map((option) => (
                  <MenuItem key={option.id} value={option.id}>
                    {option.name}
                  </MenuItem>
                ))
              )}
            </Select>
          </FormControl>
        </Grid>
      </Grid>

      {/* Site filter — options depend on the selected CMO */}
      <Grid
        style={{
          display: 'flex',
          alignItems: 'flex-start',
          gap: '10px',
          flexDirection: 'column',
          marginTop: '10px',
        }}
      >
        <Grid>
          <Typography style={{ fontWeight: 'bold', fontSize: '0.875rem' }} className="dashboard-filter-labels">
            Site / Plant:
          </Typography>
        </Grid>
        <Grid>
          <FormControl
            variant="outlined"
            size="small"
            style={{ width: '14rem', marginRight: '0.5rem' }}
            className={`dashboard-filter-dropdown ${localClasses.formControl}`}
          >
            <Select
              value={site || 'all'}
              onChange={(e) => handleSiteChange(e.target.value)}
              style={{ height: '32px', backgroundColor: 'transparent', fontSize: '0.875rem' }}
              // Options are site names (SiteName, falling back to
              // mfgPlantShortName). CMO = "All" lists every site; a specific
              // CMO narrows to that CMO's sites (matched on vendorMosymphonyID).
              disabled={siteDisabled}
              displayEmpty
              disableRipple
              disableFocusRipple
              className={localClasses.selectRoot}
              MenuProps={{ PaperProps: { style: { maxHeight: 300 } } }}
            >
              <MenuItem value="all">All</MenuItem>
              {siteOptions.length === 0 ? (
                <MenuItem value="" disabled>No data available</MenuItem>
              ) : (
                siteOptions.map((opt) => (
                  <MenuItem key={opt.value} value={opt.value}>
                    {opt.label}
                  </MenuItem>
                ))
              )}
            </Select>
          </FormControl>
        </Grid>
      </Grid>

      {/* UI-PD-Changes: Deliverable Type dropdown removed from the filters
          section (and deliverableTypeID dropped from the dashboard API
          payloads). Kept commented for easy restore.
      <Grid
        style={{
          display: 'flex',
          alignItems: 'flex-start',
          gap: '10px',
          flexDirection: 'column',
          marginTop: '10px',
        }}
      >
        <Grid>
          <Typography style={{ fontWeight: 'bold', fontSize: '0.875rem' }} className="dashboard-filter-labels">
            Deliverable Type:
          </Typography>
        </Grid>
        <Grid>
          <FormControl
            variant="outlined"
            size="small"
            style={{ width: '14rem', marginRight: '1rem' }}
            className={`dashboard-filter-dropdown ${localClasses.formControl}`}
          >
            <Select
              value={deliverableType}
              onChange={(e) => setDeliverableType(e.target.value)}
              style={{ height: '32px', backgroundColor: 'transparent', fontSize: '0.875rem' }}
              disabled={deliverableTypesList.length === 0 || filtersLoading}
              displayEmpty
              disableRipple
              disableFocusRipple
              className={localClasses.selectRoot}
              MenuProps={{ PaperProps: { style: { maxHeight: 300 } } }}
            >
              <MenuItem value="all">All</MenuItem>
              {deliverableTypesList.length === 0 ? (
                <MenuItem value="" disabled>No data available</MenuItem>
              ) : (
                deliverableTypesList.map((type) => {
                  // Handle both object format (with deliverableTypeID) and string format
                  const displayName =
                    typeof type === 'object' && type?.deliverableTypeName
                      ? type.deliverableTypeName
                      : type;
                  const value =
                    typeof type === 'object' && type?.deliverableTypeName
                      ? type.deliverableTypeName
                      : type;
                  return (
                    <MenuItem key={displayName} value={value}>
                      {displayName}
                    </MenuItem>
                  );
                })
              )}
            </Select>
          </FormControl>
        </Grid>
      </Grid>
      */}

      {/* Save filters */}
      <Grid
        style={{
          display: 'flex',
          alignItems: 'flex-start',
          gap: '20px',
          flexDirection: 'column',
        }}
      >
        <Button
          variant="contained"
          disableElevation
          disableRipple
          disableFocusRipple
          className={`${classes.stylebutton} dashboard-save-filter-btn`}
          onClick={handleSaveFilters}
          // UI-PD-Changes: deliverableType dropped from the enable check
          // (dropdown removed) — original condition kept for reference:
          // disabled={(cmoSite === initialCmoSite && deliverableType === initialDeliverableType) || filtersLoading}
          // Enable when either the Site (cmoSite) or the CMO (cmoOrgId) changed —
          // selecting a CMO alone changes cmoOrgId while cmoSite stays "all".
          disabled={(cmoSite === initialCmoSite && cmoOrgId === initialCmoOrgId) || filtersLoading}
          style={{ marginLeft: '8px', marginRight: '8px' }}
        >
          Save filters
        </Button>
      </Grid>

      {/* Clear All Filters */}
      <Grid style={{ display: 'flex' }}>
        <Typography
          variant="caption"
          style={{
            justifyContent: 'end',
            fontSize: '14px',
            color: 'blue',
            marginLeft: '8px',
            marginRight: '8px',
            cursor: 'pointer',
          }}
          className={classes.clearText}
          onClick={handleClearFilters}
        >
          Clear All Filters
        </Typography>
      </Grid>
    </Grid>
  );
};

export default FilterSection;